/**
 * Circuit Breaker Exchange for URQL
 *
 * Wraps GraphQL requests with circuit breaker protection:
 * - Opens circuit after repeated failures
 * - Fails fast when circuit is open
 * - Gradually recovers in half-open state
 */

import { CombinedError, type Exchange, type Operation, type OperationResult } from "@urql/core";
import { pipe, map, filter, merge, share } from "wonka";
import {
  CircuitOpenError,
  configureCircuit,
  CIRCUIT_PRESETS,
  getCircuitState,
  recordSuccess,
  recordFailure,
} from "@/lib/circuit-breaker";
import { circuitLog } from "@/lib/logger";

// =============================================================================
// CONSTANTS
// =============================================================================

const CIRCUIT_KEY = "graphql-api";

// Configure circuit for GraphQL API
configureCircuit(CIRCUIT_KEY, CIRCUIT_PRESETS.api);

// =============================================================================
// EXCHANGE
// =============================================================================

/**
 * Circuit breaker exchange for URQL.
 *
 * When circuit is OPEN:
 * - Returns error immediately (fail fast)
 *
 * When circuit is CLOSED/HALF_OPEN:
 * - Forwards requests normally
 * - Tracks server errors
 */
export const circuitBreakerExchange: Exchange =
  ({ forward }) =>
  (ops$) => {
    // Share the operations stream
    const sharedOps$ = share(ops$);

    // Operations that should be blocked (circuit open + not teardown)
    const blockedOps$ = pipe(
      sharedOps$,
      filter((op) => {
        if (op.kind === "teardown") return false;
        return getCircuitState(CIRCUIT_KEY) === "OPEN";
      }),
      map((operation) => {
        circuitLog.warn("Request blocked - circuit OPEN", {
          operation: getOperationName(operation),
          kind: operation.kind,
        });
        return createCircuitOpenResult(operation);
      })
    );

    // Operations that should be forwarded (circuit closed/half-open or teardown)
    const forwardedOps$ = pipe(
      sharedOps$,
      filter((op) => {
        if (op.kind === "teardown") return true;
        return getCircuitState(CIRCUIT_KEY) !== "OPEN";
      })
    );

    // Forward and track server errors
    const results$ = pipe(
      forward(forwardedOps$),
      map((result) => {
        if (isServerError(result)) {
          circuitLog.warn("Server error detected", {
            operation: getOperationName(result.operation),
          });
          recordFailure(CIRCUIT_KEY);
        } else if (result.data && !result.error) {
          recordSuccess(CIRCUIT_KEY);
        }
        return result;
      })
    );

    // Merge blocked results with forwarded results
    return merge([blockedOps$, results$]);
  };

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Get operation name for logging.
 */
function getOperationName(operation: Operation): string {
  const definition = operation.query.definitions[0];
  if (definition && "name" in definition && definition.name) {
    return definition.name.value;
  }
  return "unknown";
}

/**
 * Create error result for circuit open state.
 */
function createCircuitOpenResult(operation: Operation): OperationResult {
  const circuitError = new CircuitOpenError(CIRCUIT_KEY);

  // Use proper CombinedError from URQL to avoid unsafe type assertions
  const error = new CombinedError({
    networkError: circuitError,
    graphQLErrors: [],
  });

  return {
    operation,
    data: undefined,
    error,
    extensions: undefined,
    hasNext: false,
    stale: false,
  };
}

/**
 * Check if result indicates a server error that should trip the circuit.
 * We trip on:
 * - Network errors (connection failure, timeout)
 * - HTTP 5xx errors
 * - GraphQL errors with INTERNAL_SERVER_ERROR code
 * 
 * We do NOT trip on:
 * - HTTP 4xx errors (client errors)
 * - GraphQL business logic errors (UNAUTHENTICATED, FORBIDDEN, etc.)
 */
function isServerError(result: OperationResult | null | undefined): boolean {
  if (!result) return false;
  
  const error = result.error;
  if (!error) return false;

  // Check network errors
  if (error.networkError) {
    const networkError = error.networkError as Error & {
      response?: Response;
    };

    // HTTP 5xx status
    if (networkError.response?.status && networkError.response.status >= 500) {
      return true;
    }

    // Network failure (no response) - connection error, timeout, etc.
    if (!networkError.response) {
      return true;
    }
  }

  // Check GraphQL errors for server-side issues
  if (error.graphQLErrors?.length) {
    const hasServerError = error.graphQLErrors.some((gqlError) => {
      const code = gqlError.extensions?.code;
      // Only trip on internal server errors, not auth/validation errors
      return (
        code === "INTERNAL_SERVER_ERROR" ||
        code === "SERVICE_UNAVAILABLE" ||
        gqlError.message?.toLowerCase().includes("internal server error")
      );
    });
    if (hasServerError) {
      return true;
    }
  }

  return false;
}

// =============================================================================
// EXPORTS
// =============================================================================

/**
 * Check if GraphQL API circuit is healthy.
 */
export function isGraphQLApiHealthy(): boolean {
  return getCircuitState(CIRCUIT_KEY) === "CLOSED";
}

/**
 * Get current GraphQL API circuit state.
 */
export function getGraphQLCircuitState() {
  return getCircuitState(CIRCUIT_KEY);
}
